import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Badge, Button } from '@opendate-app/setlist';
import UserManualArticle from './UserManualArticle';
import UserManualArticleEditor from './UserManualArticleEditor';
import humanizeSlug from '../utils/humanizeSlug';

const KEEP_BOTH = 'both';

const resolutionMessage = (result, titleFor) => {
  if (result.kept.length > 1) return 'Kept both. The pair is off the list.';

  const parts = [`Kept "${titleFor(result.kept[0])}" and deleted "${titleFor(result.removed)}".`];
  if (result.published) parts.push('It is now published in its place.');
  if (result.relinked.length > 0) {
    parts.push(`Updated links in ${result.relinked.length} article${result.relinked.length === 1 ? '' : 's'}.`);
  }
  if (result.republished.length > 0) {
    parts.push(`Republished ${result.republished.length} so the new link is live.`);
  }
  return parts.join(' ');
};

const UserManualDuplicateCompare = ({ pair, onResolved, onNavigate }) => {
  const [articles, setArticles] = useState({});
  const [editingSlug, setEditingSlug] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const slugs = pair.articles.map((article) => article.slug);

  useEffect(() => {
    let cancelled = false;
    setArticles({});
    pair.articles.forEach(({ slug }) => {
      axios
        .get(`/user_manual/drafts/${slug}.json`)
        .then((response) => {
          if (!cancelled) setArticles((previous) => ({ ...previous, [slug]: response.data }));
        })
        .catch(() => {
          if (!cancelled) setArticles((previous) => ({ ...previous, [slug]: { loadError: true } }));
        });
    });
    return () => { cancelled = true; };
  }, [pair, reloadToken]);

  const titleFor = (slug) => articles[slug]?.title || humanizeSlug(slug);

  const confirmationFor = (keep) => {
    if (keep === KEEP_BOTH) {
      return `Keep both "${titleFor(slugs[0])}" and "${titleFor(slugs[1])}"? They'll be marked as not duplicates.`;
    }
    const drop = pair.articles.find((article) => article.slug !== keep);
    const kept = pair.articles.find((article) => article.slug === keep);
    const replacesLiveArticle = drop.published && !kept.published
      ? ` "${titleFor(drop.slug)}" is live, so "${titleFor(keep)}" will be published in its place.`
      : '';
    return `Keep "${titleFor(keep)}" and delete "${titleFor(drop.slug)}"?${replacesLiveArticle} Links to the deleted article will point to the one you keep.`;
  };

  const handleResolve = (keep) => {
    if (!window.confirm(confirmationFor(keep))) return;

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    setResolving(true);
    axios
      .post(
        '/user_manual/duplicate_resolutions.json',
        { articles: slugs, keep },
        { headers: { 'X-CSRF-Token': csrfToken } },
      )
      .then((response) => {
        toast.success(resolutionMessage(response.data, titleFor));
        onResolved();
      })
      .catch((error) => {
        setResolving(false);
        toast.error(error.response?.data?.error || "Couldn't resolve that pair. Try again.");
      });
  };

  const handleSaved = () => {
    setEditingSlug(null);
    setReloadToken((token) => token + 1);
  };

  return (
    <div className="user-manual-compare">
      <div className="user-manual-compare-header">
        <div>
          <div className="user-manual-compare-title">Possible duplicate</div>
          <div className="user-manual-compare-meta">
            {pair.likely ? 'Likely duplicate' : 'May be a separate how-to'}
            {' · '}
            {`${Math.round(pair.score * 100)}% similar`}
          </div>
        </div>
        <Button size="sm" onClick={() => handleResolve(KEEP_BOTH)} disabled={resolving || editingSlug !== null}>
          Keep both
        </Button>
      </div>
      <div className="user-manual-compare-columns">
        {pair.articles.map((summary) => {
          const article = articles[summary.slug];
          return (
            <section key={summary.slug} className="user-manual-compare-column">
              <div className="user-manual-compare-column-bar">
                <Badge size="sm" color={summary.published ? 'success' : 'gray'}>
                  {summary.published ? 'Published' : 'Draft'}
                </Badge>
                <div className="user-manual-compare-column-actions">
                  <Button
                    size="sm"
                    variant="tertiary"
                    onClick={() => setEditingSlug(summary.slug)}
                    disabled={resolving || editingSlug !== null}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => handleResolve(summary.slug)}
                    disabled={resolving || editingSlug !== null || !article || article.loadError}
                  >
                    Keep this one
                  </Button>
                </div>
              </div>
              {editingSlug === summary.slug && (
                <UserManualArticleEditor slug={summary.slug} onSaved={handleSaved} onCancel={() => setEditingSlug(null)} />
              )}
              {editingSlug !== summary.slug && !article && (
                <div className="user-manual-compare-status">Loading…</div>
              )}
              {editingSlug !== summary.slug && article?.loadError && (
                <div className="user-manual-draft-banner user-manual-draft-banner-error">
                  <span>Couldn't load this article.</span>
                </div>
              )}
              {editingSlug !== summary.slug && article && !article.loadError && (
                <UserManualArticle
                  section={{ title: humanizeSlug(summary.section) }}
                  chapter={{ title: humanizeSlug(summary.chapter) }}
                  article={article}
                  prevArticle={null}
                  nextArticle={null}
                  onNavigate={onNavigate}
                />
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default UserManualDuplicateCompare;
